// Organiser-only photo manager. The organiser uploads a zip of JPEGs, which the
// server processes asynchronously (orient → resize → watermark → WebP) and
// publishes to the public gallery. Photos are not part of the tournament
// snapshot, so this view fetches them on demand from the authenticated
// `/photos` endpoint and polls while any are still processing. Each photo can be
// captioned (shown on the public page) or deleted.

import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CollectionsIcon from '@mui/icons-material/Collections';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { PHOTO_COMMENT_MAX, PHOTO_MAX_COUNT, type PhotoStatus, type TournamentPhoto } from '../domain';
import { deletePhoto, fetchPhotos, setPhotoComment, uploadPhotoZip } from '../data/apiClient';
import { useTournament } from '../state/TournamentContext';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';
import { PhotoLightbox, type LightboxPhoto } from './PhotoLightbox';

// After an upload the zip is processed asynchronously (direct-to-storage upload →
// notification → ingest), so the photos appear a moment later. Keep polling for
// this long after an upload even before any PROCESSING row exists yet.
const INGEST_WAIT_MS = 90_000;

// Status labels are translated per-render (`admin.photos.status.*`); only the
// chip colour is fixed per status.
const STATUS_COLOR: Record<PhotoStatus, 'default' | 'success' | 'error'> = {
  PROCESSING: 'default',
  READY: 'success',
  FAILED: 'error',
};

export function PhotosView() {
  const { tournament } = useTournament();
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  // Without MANAGE_PHOTOS the view is a read-only gallery: no upload, delete
  // or caption editing (the server refuses those mutations regardless).
  const canManage = hasPermission('MANAGE_PHOTOS');
  const id = tournament?.id ?? null;

  const [photos, setPhotos] = useState<TournamentPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const inFlight = useRef(false);
  // Latest photos + the time until which we keep polling for freshly-uploaded
  // ones — read from the interval without re-subscribing it every render.
  const photosRef = useRef(photos);
  const pollUntilRef = useRef(0);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  // The data loaders below must not re-run when the language changes (their
  // effects would refetch and flash the view), so they read the translator
  // through a ref that always holds the latest `t` without changing identity.
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    if (!id || inFlight.current) return;
    inFlight.current = true;
    try {
      setPhotos(await fetchPhotos(id));
      setError(null);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : tRef.current('admin.photos.errors.load'),
      );
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  // Poll while any photo is still processing — or briefly after an upload, before
  // the ingested photos have appeared — so the grid updates without a manual
  // refresh. A single timer checks refs, so it is never re-subscribed.
  useEffect(() => {
    const timer = window.setInterval(() => {
      const active =
        photosRef.current.some((p) => p.status === 'PROCESSING') ||
        Date.now() < pollUntilRef.current;
      if (active) void load();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [load]);

  const onFile = useCallback(
    async (file: File | null) => {
      if (!file || !id) return;
      if (!/\.zip$/i.test(file.name)) {
        setError(t('admin.photos.errors.notZip'));
        return;
      }
      setUploading(true);
      setError(null);
      try {
        await uploadPhotoZip(id, file);
        // Processing is async — keep polling until the photos show up, then flip
        // from Processing to Published.
        pollUntilRef.current = Date.now() + INGEST_WAIT_MS;
        await load();
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : t('admin.photos.errors.upload'));
      } finally {
        setUploading(false);
      }
    },
    [id, load, t],
  );

  const onDelete = useCallback(
    async (photoId: string) => {
      if (!id) return;
      setError(null);
      try {
        setPhotos(await deletePhoto(id, photoId));
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : t('admin.photos.errors.delete'));
      }
    },
    [id, t],
  );

  const onComment = useCallback(
    async (photoId: string, comment: string) => {
      if (!id) return;
      setError(null);
      try {
        setPhotos(await setPhotoComment(id, photoId, comment));
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : t('admin.photos.errors.saveCaption'));
      }
    },
    [id, t],
  );

  if (!tournament) return null;

  const published = photos.filter((p) => p.status === 'READY').length;
  const atCapacity = photos.length >= PHOTO_MAX_COUNT;

  // Only published photos have an image to view; the lightbox navigates that set.
  const readyPhotos: LightboxPhoto[] = photos
    .filter((p) => p.status === 'READY' && p.webpUrl)
    .map((p) => ({ id: p.id, url: p.webpUrl as string, comment: p.comment }));
  const openLightbox = (photoId: string) => {
    const i = readyPhotos.findIndex((p) => p.id === photoId);
    if (i >= 0) setLightboxIndex(i);
  };

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 3, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {t('tabs.photos')}
        </Typography>
        <Chip variant="outlined" label={`${photos.length} / ${PHOTO_MAX_COUNT}`} />
        {published > 0 && (
          <Chip color="success" label={t('admin.photos.publishedCount', { count: published })} />
        )}
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void load()}
          disabled={loading}
        >
          {t('actions.refresh')}
        </Button>
        {canManage && (
          <Tooltip title={atCapacity ? t('admin.photos.atCapacity', { count: PHOTO_MAX_COUNT }) : ''}>
            <span>
              <Button
                variant="contained"
                component="label"
                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                disabled={uploading || atCapacity}
              >
                {t('admin.photos.uploadZip')}
                <input
                  hidden
                  type="file"
                  accept=".zip,application/zip,application/x-zip-compressed"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    e.target.value = ''; // allow re-selecting the same file
                    void onFile(file);
                  }}
                />
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      {canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {/* {zip}/{jpeg} stay literal in the template (only {max} is passed),
              so the technical tokens can be re-inserted with emphasis. */}
          {t('admin.photos.uploadHint', { max: PHOTO_MAX_COUNT })
            .split(/(\{zip\}|\{jpeg\})/)
            .map((part, i) =>
              part === '{zip}' ? (
                <strong key={i}>.zip</strong>
              ) : part === '{jpeg}' ? (
                <strong key={i}>JPEG</strong>
              ) : (
                part
              ),
            )}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        {loading && photos.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : photos.length === 0 ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
            <CollectionsIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {t('admin.photos.empty')}
            </Typography>
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 2,
            }}
          >
            {photos.map((photo) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                readOnly={!canManage}
                onDelete={() => void onDelete(photo.id)}
                onSaveComment={(c) => void onComment(photo.id, c)}
                onOpen={
                  photo.status === 'READY' && photo.webpUrl
                    ? () => openLightbox(photo.id)
                    : undefined
                }
              />
            ))}
          </Box>
        )}
      </Paper>

      <PhotoLightbox
        photos={readyPhotos}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        showDownload
        labels={{
          close: t('gallery.close'),
          download: t('gallery.download'),
          previous: t('gallery.previous'),
          next: t('gallery.next'),
          alt: t('gallery.alt'),
        }}
      />
    </Box>
  );
}

interface PhotoCardProps {
  photo: TournamentPhoto;
  /** Hide the delete / caption controls (viewer lacks MANAGE_PHOTOS). */
  readOnly: boolean;
  onDelete: () => void;
  onSaveComment: (comment: string) => void;
  /** Open this photo fullscreen (only for published photos). */
  onOpen?: () => void;
}

function PhotoCard({ photo, readOnly, onDelete, onSaveComment, onOpen }: PhotoCardProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(photo.comment);
  const dirty = draft.trim() !== photo.comment;

  // Re-sync the editor when the stored caption changes (after a save, or an edit
  // from another tab). Keyed on the value, so the 3s processing poll — which
  // returns the same caption — never clobbers what the organiser is typing.
  useEffect(() => {
    setDraft(photo.comment);
  }, [photo.comment]);

  return (
    <Paper
      variant="outlined"
      sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '4 / 3',
          bgcolor: 'action.hover',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {photo.status === 'READY' && photo.webpUrl ? (
          <Box
            component="img"
            src={photo.webpUrl}
            alt={photo.comment || t('gallery.alt')}
            loading="lazy"
            onClick={onOpen}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              cursor: onOpen ? 'zoom-in' : 'default',
            }}
          />
        ) : photo.status === 'FAILED' ? (
          <BrokenImageIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
        ) : (
          <CircularProgress size={28} />
        )}
        <Chip
          size="small"
          color={STATUS_COLOR[photo.status]}
          label={t(`admin.photos.status.${photo.status}`)}
          sx={{ position: 'absolute', top: 8, left: 8 }}
        />
        {!readOnly && (
          <IconButton
            size="small"
            aria-label={t('admin.photos.deletePhoto')}
            onClick={onDelete}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              bgcolor: 'rgba(0,0,0,0.45)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ p: 1.5 }}>
        {readOnly ? (
          <Typography variant="body2" color="text.secondary">
            {photo.comment || t('admin.photos.noCaption')}
          </Typography>
        ) : (
          <>
            <TextField
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, PHOTO_COMMENT_MAX))}
              placeholder={t('admin.photos.captionPlaceholder')}
              size="small"
              fullWidth
              multiline
              maxRows={3}
            />
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button
                size="small"
                startIcon={<SaveIcon fontSize="small" />}
                disabled={!dirty}
                onClick={() => onSaveComment(draft.trim())}
              >
                {t('admin.photos.saveCaption')}
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </Paper>
  );
}
